/**
 * Create a draft offer for the selected application — `POST
 * /recruiting/applications/{id}/offers`, WP-W5.
 *
 * Every field is optional; the openapi accepts an empty body and opens a bare `draft` to
 * be filled in and approved later. So there is nothing to make the submit wait for — an
 * application is already chosen upstream (the form is only mounted once one is), and a
 * blank field is **omitted** rather than sent, because `exactOptionalPropertyTypes` makes
 * an absent key and an empty string different requests.
 *
 * ── The money is a string, and stays one ─────────────────────────────────────
 *
 * `salary_amount` is a `NUMERIC(14,2)` on the wire, carried as an exact decimal string so
 * no cent is lost to a float. The field is a plain text input validated against the wire
 * pattern before submit; the currency is three uppercase letters, upper-cased as typed.
 * `start_date` is a calendar day (`type="date"`); `expires_at` is an instant captured as
 * `datetime-local` and sent as an ISO instant.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { RecruitingOffer, RecruitingOfferCreateRequest } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

/** The wire pattern for `salary_amount`: up to 12 integer and 2 decimal digits. */
const AMOUNT_PATTERN = /^(0|[1-9][0-9]{0,11})(\.[0-9]{1,2})?$/;

export interface CreateOfferFormProps {
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly created: RecruitingOffer | null;
  readonly onSubmit: (body: RecruitingOfferCreateRequest) => void;
}

/** A `datetime-local` value → an ISO instant, or `undefined` when blank/unparseable. */
function toInstant(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function CreateOfferForm({
  pending,
  error,
  created,
  onSubmit,
}: CreateOfferFormProps): ReactNode {
  const { t } = useI18n();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [startDate, setStartDate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [documentKey, setDocumentKey] = useState('');

  const trimmedAmount = amount.trim();
  const amountValid = trimmedAmount === '' || AMOUNT_PATTERN.test(trimmedAmount);

  const build = (): RecruitingOfferCreateRequest => {
    const curr = currency.trim().toUpperCase();
    const date = startDate.trim();
    const expiresIso = toInstant(expiresAt);
    const key = documentKey.trim();
    return {
      ...(trimmedAmount === '' ? {} : { salary_amount: trimmedAmount }),
      ...(curr === '' ? {} : { salary_currency: curr }),
      ...(date === '' ? {} : { start_date: date }),
      ...(expiresIso === undefined ? {} : { expires_at: expiresIso }),
      ...(key === '' ? {} : { offer_document_s3_key: key }),
    };
  };

  return (
    <section className="wp-section" aria-labelledby="w5-create-heading" data-testid="w5-create">
      <h2 className="wp-section__title" id="w5-create-heading">
        {t('recruiting.offers.create.title')}
      </h2>
      <p className="wp-small wp-muted">{t('recruiting.offers.create.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (pending || !amountValid) return;
          onSubmit(build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w5-create-amount">
            {t('recruiting.offers.create.amount')}
          </label>
          <input
            className="wp-field__input"
            id="w5-create-amount"
            data-testid="w5-create-amount"
            type="text"
            inputMode="decimal"
            value={amount}
            autoComplete="off"
            placeholder="85000.00"
            onChange={(event) => {
              setAmount(event.target.value);
            }}
          />
          {amountValid ? null : (
            <p className="wp-small" data-testid="w5-create-amount-invalid">
              {t('recruiting.offers.create.amountInvalid')}
            </p>
          )}
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w5-create-currency">
            {t('recruiting.offers.create.currency')}
          </label>
          <input
            className="wp-field__input"
            id="w5-create-currency"
            data-testid="w5-create-currency"
            type="text"
            value={currency}
            autoComplete="off"
            maxLength={3}
            placeholder="USD"
            onChange={(event) => {
              setCurrency(event.target.value.toUpperCase());
            }}
          />
        </div>

        <DateField
          className="wp-filters__group"
          id="w5-create-start-date"
          testId="w5-create-start-date"
          label={t('recruiting.offers.create.startDate')}
          value={startDate}
          onChange={setStartDate}
        />

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w5-create-expires-at">
            {t('recruiting.offers.create.expiresAt')}
          </label>
          <input
            className="wp-field__input"
            id="w5-create-expires-at"
            data-testid="w5-create-expires-at"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => {
              setExpiresAt(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w5-create-document">
            {t('recruiting.offers.create.documentKey')}
          </label>
          <input
            className="wp-field__input"
            id="w5-create-document"
            data-testid="w5-create-document"
            type="text"
            value={documentKey}
            autoComplete="off"
            placeholder="offers/2026/candidate-42.pdf"
            onChange={(event) => {
              setDocumentKey(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.offers.create.documentKeyHint')}</p>
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w5-create-submit"
            disabled={pending || !amountValid}
          >
            {pending
              ? t('recruiting.offers.create.submitting')
              : t('recruiting.offers.create.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.offers.subjectWord')}
          testId="w5-create-error"
        />
      )}
      {created === null ? null : (
        <StatusNote severity="low" title={t('recruiting.offers.create.ok')} testId="w5-create-ok" />
      )}
    </section>
  );
}
